import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiExternalLink } from "react-icons/fi";
import { typography, spacing, gradients } from "../styles/designSystem";
import { getScrollVisibility, useScrollProgress } from "../styles/animations";
import { projectsPreview } from "../data/projectsData";

const ProjectCard = ({ project, visibility, index }) => {
  const mockRef = useRef(null);
  const imageRef = useRef(null);
  const glareRef = useRef(null);
  const rafRef = useRef(null);

  const handleMouseMove = (event) => {
    const mock = mockRef.current;
    const image = imageRef.current;
    const glare = glareRef.current;

    if (!mock || !image || !glare) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      const rect = mock.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      const x = mouseX / rect.width - 0.5;
      const y = mouseY / rect.height - 0.5;

      mock.style.transform = `
        perspective(1000px)
        rotateX(${-y * 8}deg)
        rotateY(${x * 10}deg)
        translate3d(0, -5px, 18px)
      `;

      image.style.transform = `
        translate3d(${x * -12}px, ${y * -10}px, 30px)
        scale(1.045)
      `;

      glare.style.opacity = "1";
      glare.style.background = `
        radial-gradient(
          circle at ${mouseX}px ${mouseY}px,
          rgba(255,255,255,0.42),
          rgba(255,255,255,0.12) 20%,
          transparent 45%
        )
      `;
    });
  };

  const resetTransform = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    if (mockRef.current) {
      mockRef.current.style.transform = `
        perspective(1000px)
        rotateX(0deg)
        rotateY(0deg)
        translate3d(0, 0, 0)
      `;
    }

    if (imageRef.current) {
      imageRef.current.style.transform = "translate3d(0, 0, 0) scale(1)";
    }

    if (glareRef.current) glareRef.current.style.opacity = "0";
  };

  return (
    <article
      className="group relative overflow-hidden rounded-[2rem] border border-white/12 bg-white/[0.055] p-4 shadow-2xl shadow-black/20 backdrop-blur-sm transition-colors duration-300 hover:border-white/20 sm:p-5"
      style={{
        opacity: visibility,
        transform: `translateY(${(1 - visibility) * (35 + index * 12)}px)`,
        transition: "transform 0.08s linear, opacity 0.12s linear",
      }}
    >
      <div className={`absolute -inset-16 ${project.glow} blur-3xl`} />

      <div
        className="relative"
        onMouseMove={handleMouseMove}
        onMouseLeave={resetTransform}
      >
        <div
          ref={mockRef}
          className="relative rounded-[1.35rem] border border-white/15 bg-white/[0.07] p-2.5 shadow-xl shadow-black/30 transition-transform duration-300 ease-out will-change-transform [transform-style:preserve-3d]"
        >
          <div className="overflow-hidden rounded-[1rem] bg-white [transform:translateZ(20px)]">
            <div className="flex items-center gap-1.5 border-b border-gray-200/80 bg-gray-50 px-3 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
              <span className="ml-2 truncate text-[11px] text-gray-500">
                {project.domain}
              </span>
            </div>

            <div className="relative h-[220px] overflow-hidden sm:h-[240px] lg:h-[260px]">
              <img
                ref={imageRef}
                src={project.preview}
                alt={project.alt}
                className="h-full w-full object-cover object-top transition-transform duration-300 ease-out will-change-transform"
              />

              <div
                ref={glareRef}
                className="pointer-events-none absolute inset-0 z-20 opacity-0 mix-blend-screen transition-opacity duration-300"
              />

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/10" />
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-8 px-1 pb-1">
        <h3 className={`${typography.heading.h4} text-white`}>
          {project.title}
        </h3>

        <p className="mt-3 text-sm font-light leading-relaxed text-white/70 sm:text-[15px]">
          {project.description}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {project.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-white/12 bg-white/[0.07] px-3 py-1.5 text-[11px] font-medium text-white/75"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to={project.projectPath}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-semibold text-gray-950 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20"
          >
            View project
            <FiArrowRight />
          </Link>

          <a
            href={project.website}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/10"
          >
            Visit website
            <FiExternalLink />
          </a>
        </div>
      </div>
    </article>
  );
};

const Projects = () => {
  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef);
  const visibility = getScrollVisibility(progress);

  return (
    <section
      id="projects-overview"
      ref={sectionRef}
      className={`relative overflow-hidden py-20 pb-28 text-white md:py-24 md:pb-32 ${gradients.dark}`}
    >
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 md:h-14">
        <svg
          className="h-full w-full"
          viewBox="0 0 1440 64"
          preserveAspectRatio="none"
          fill="white"
        >
          <path d="M0,64 L1440,64 L1440,32 C1080,0 360,16 0,0 Z" />
        </svg>
      </div>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(230,126,34,0.11),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(214,179,106,0.09),transparent_38%)]" />

      <div
        className="animate-drift pointer-events-none absolute -left-16 top-16 h-44 w-44 rounded-full bg-primary/[0.11] blur-3xl"
        style={{
          transform: `translate(${(1 - visibility) * -25}px, ${(1 - visibility) * -18}px)`,
        }}
      />

      <div
        className="pattern-dot-grid pointer-events-none absolute right-4 top-8 h-24 w-24 rounded-xl"
        style={{ opacity: 0.18 * visibility }}
      />

      <div className={`relative ${spacing.container}`}>
        <div
          className="mb-9 max-w-2xl md:mb-11"
          style={{
            opacity: visibility,
            transform: `translateY(${(1 - visibility) * 24}px)`,
            transition: "transform 0.08s linear, opacity 0.12s linear",
          }}
        >
          <p className={`${typography.label} text-white/55`}>Selected work</p>
          <h2 className={`${typography.heading.h2} mt-3 text-white`}>
            Featured Projects
          </h2>
          <div
            className="mt-4 h-1 w-14 rounded-full bg-gradient-to-r from-primary to-accent"
            style={{
              transform: `scaleX(${visibility})`,
              transformOrigin: "left",
            }}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2 lg:gap-7">
          {projectsPreview.map((project, index) => (
            <ProjectCard
              key={project.title}
              project={project}
              visibility={visibility}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
